<script setup lang="ts">
import ServerOff from 'lucide-vue-next/dist/esm/icons/server-off.js';
import NxButton from '../Button/NxButton.vue';

export type NxModuleGateState = 'checking' | 'available' | 'unavailable';

const props = withDefaults(
  defineProps<{
    state: NxModuleGateState;
    title?: string;
    hint?: string;
    checkingLabel?: string;
    retryLabel?: string;
  }>(),
  {
    title: undefined,
    hint: undefined,
    checkingLabel: '…',
    retryLabel: undefined,
  }
);

const emit = defineEmits<{
  (e: 'retry'): void;
}>();
</script>

<template>
  <slot v-if="props.state === 'available'" />

  <div
    v-else-if="props.state === 'checking'"
    class="nx-module-gate nx-module-gate-checking"
    role="status"
    aria-live="polite"
  >
    {{ props.checkingLabel }}
  </div>

  <div v-else class="nx-module-gate nx-module-gate-unavailable" role="alert">
    <div class="nx-module-gate-icon" aria-hidden="true">
      <ServerOff class="nxt-icon-xl" :stroke-width="1.8" />
    </div>
    <div v-if="props.title" class="nx-module-gate-title">{{ props.title }}</div>
    <div v-if="props.hint" class="nx-module-gate-hint">{{ props.hint }}</div>
    <div v-if="props.retryLabel" class="nx-module-gate-actions">
      <NxButton variant="outline" height="s" @click="emit('retry')">
        {{ props.retryLabel }}
      </NxButton>
    </div>
  </div>
</template>

<style scoped>
.nx-module-gate {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(var(--nxt-size-unit) * 10);
  padding: var(--nxt-space-xl) var(--nxt-space-md);
  text-align: center;
}

.nx-module-gate-checking {
  min-height: var(--nxt-area-size-sm);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}

.nx-module-gate-unavailable {
  gap: var(--nxt-space-xs);
  color: var(--nxt-fg-subtle);
}

.nx-module-gate-icon {
  display: grid;
  width: var(--nxt-tile-size-xl);
  height: var(--nxt-tile-size-xl);
  place-items: center;
  margin-bottom: calc(var(--nxt-space-2xs) / 2);
  border-radius: 50%;
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-subtle);
}

.nx-module-gate-title {
  max-width: 42ch;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.3;
}

.nx-module-gate-hint {
  max-width: 44ch;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.nx-module-gate-actions {
  display: flex;
  justify-content: center;
  margin-top: var(--nxt-space-2xs);
}
</style>
